import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { PrimeNgModule } from '../primeng/primeng.module';
import { DatePipe } from '@angular/common';
import { RegistrationWidgetService } from './registration-widget.service';
import { CreditCardFeeType } from 'src/app/shared/models/creditcard-fee-type.enum';
import { StripeElementsDirective, StripePaymentElementComponent, StripeService, injectStripe } from 'ngx-stripe';
import { StripeElementsOptions, StripePaymentElementOptions } from '@stripe/stripe-js';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AlertDialogPrimeNg } from '../primeng/dialogs/alert.dialog';
import { takeUntil } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { Subject, Subscription, of, Observable } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { PaymentType } from 'src/app/shared/models/payment-type-enum';
import { environment } from 'src/environments/environment';

type RegistrationType = 'individual' | 'team';

/** Single golfer: lead a new team vs join an existing captain’s team (only when join is available). */
type SingleRegistrationTeamMode = 'solo_captain' | 'join_team';

@Component({
  selector: 'registration-widget',
  imports: [PrimeNgModule, DatePipe, StripeElementsDirective, StripePaymentElementComponent],
  templateUrl: './registration-widget.component.html',
  styleUrl: './registration-widget.component.scss',
  standalone: true
})
export class RegistrationWidgetComponent implements OnInit, OnDestroy {
  readonly stripe = injectStripe();

  @ViewChild(StripeElementsDirective) elements!: StripeElementsDirective;
  @ViewChild(StripePaymentElementComponent) paymentElement!: StripePaymentElementComponent;

  public elementsOptions: StripeElementsOptions = {
    appearance: { theme: 'stripe' },
    locale: 'en',
  };

  public paymentElementsOptions: StripePaymentElementOptions = {
    layout: { type: 'tabs', defaultCollapsed: false }
  };

  public registrationCode = '';
  public outing: any | null = null;
  public registrationType: RegistrationType = 'individual';
  public registrationForm: FormGroup;
  public createdRegistrations: any[] = [];
  public paymentIntent: any = null;
  /** Stepper uses 1-based values: 1 = Find Outing, 2 = Add Golfers, 3 = Review. */
  public activeStep = 1;
  public isOutingPast = false;
  public teamCaptainErrorMessage: string | null = null;
  public isEmailModalOpen = false;
  public emailSending = false;
  public isPaymentSuccessOpen = false;
  public teamCaptains: any[] = [];
  CreditCardFeeType = CreditCardFeeType;

  public loadingStates = {
    search: false,
    submit: false,
    payment: false,
    paymentSubmit: false,
    playerSearch: false
  };

  public messages: { severity: string; summary: string } | null = null;
  public isCardValid = false;
  public showPaymentStep = false;
  public registrationComplete = false;
  public selectedTeamCaptainIndex = -1;

  /** Shown when registering one player and existing teams allow joining. */
  public singleRegistrationTeamMode: SingleRegistrationTeamMode = 'solo_captain';

  private singlePlayerCaptainSub?: Subscription;

  public registrationTypeOptions = [
    { label: 'Individual', value: 'individual' },
    { label: 'Team (2-4 golfers)', value: 'team' }
  ];

  public playerRegistrationCode = '';
  public playerRegistration: any | null = null;
  public playerSearchError: string | null = null;
  public playerPaymentIntent: any = null;
  public showPlayerPaymentStep = false;

  private dialogRef: DynamicDialogRef;
  private apiKey: string | null = null;
  private destroyed$ = new Subject<void>();
  route = inject(ActivatedRoute);
  public userDetails: any;

  constructor(
    private registrationWidgetService: RegistrationWidgetService,
    private formBuilder: FormBuilder,
    private ngxStripeService: StripeService,
    private dialogService: DialogService
  ) {
    this.registrationForm = this.formBuilder.group({
      teamCaptain: new FormControl('', Validators.required),
      stripePaymentIntentId: new FormControl(''),
      emailAddress: new FormControl('', [Validators.required, Validators.email]),
      registrations: this.formBuilder.array([])
    });
  }

  ngOnDestroy(): void {
    this.singlePlayerCaptainSub?.unsubscribe();
  }

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntil(this.destroyed$)).subscribe((params: any) => {
      this.apiKey = params.get('apiKey');
      this.registrationWidgetService.getUserDetails(this.apiKey).subscribe({
        next: (result: any) => {
          this.userDetails = result;
        },
        error: () => {
          this.setMessage('Failed to get user details.', 'error');
        }
      });
      this.destroyed$.unsubscribe();
    });
  }

  /** Loads team captain list; used after outing search so step 1 can render with full data. */
  private loadTeamCaptains$(): Observable<void> {
    if (!this.apiKey || !this.outing?.outingIterationId) {
      return of(undefined);
    }
    return this.registrationWidgetService.getAllTeamCaptains(this.apiKey, this.outing.outingIterationId).pipe(
      tap((result) => {
        this.teamCaptains = Array.isArray(result) ? result : [];
        this.applySinglePlayerTeamCaptain();
      }),
      catchError(() => {
        this.teamCaptains = [];
        return of(undefined);
      }),
      map(() => undefined)
    );
  }

  getTeamCaptains(): void {
    this.loadTeamCaptains$().subscribe();
  }

  get teamCaptainSelectOptions(): { label: string; value: string; disabled: boolean }[] {
    return (this.teamCaptains || []).map((t) => ({
      label: t.teamCaptain + (t.numberOfGolfers === 4 ? ' (Full)' : ''),
      value: t.teamCaptain,
      disabled: t.numberOfGolfers === 4
    }));
  }

  get hasSelectableTeamCaptain(): boolean {
    return this.teamCaptains.some((t) => t.numberOfGolfers < 4);
  }

  get allTeamCaptainSlotsFull(): boolean {
    return this.teamCaptains.length > 0 && this.teamCaptains.every((t) => t.numberOfGolfers === 4);
  }

  get registrations(): FormArray {
    return this.registrationForm.get('registrations') as FormArray;
  }

  get subtotal(): number {
    return this.outing ? this.outing.cost * this.registrations.length : 0;
  }

  get creditCardFeeAmount(): number {
    if (!this.outing?.creditCardFee || this.outing.creditCardFee === 0) return 0;
    if (this.outing.creditCardFeeType === CreditCardFeeType.Percentage) {
      return this.subtotal * (this.outing.creditCardFee / 100);
    }
    if (this.outing.creditCardFeeType === CreditCardFeeType.Fixed) {
      return this.outing.creditCardFee;
    }
    return this.subtotal * ((this.outing.creditCardFee || 0) / 100);
  }

  get totalCost(): number {
    return this.subtotal + this.creditCardFeeAmount;
  }

  get canProceedToReview(): boolean {
    const hasNames = this.registrations.controls.some(
      (r: any) => r.get('firstName')?.value && r.get('lastName')?.value
    );
    if (!hasNames) {
      return false;
    }
    if (this.registrations.length === 1 && this.allTeamCaptainSlotsFull) {
      return false;
    }
    if (this.registrations.length === 1 && this.hasSelectableTeamCaptain) {
      if (this.singleRegistrationTeamMode === 'join_team') {
        const tc = this.registrationForm.get('teamCaptain')?.value;
        if (tc == null || String(tc).trim() === '') {
          return false;
        }
      }
    }
    return true;
  }

  private newRegistrationGroup(): FormGroup {
    const group = this.formBuilder.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      startingHole: [''],
      outingIterationId: [this.outing?.outingIterationId || '']
    });
    if (this.outing?.outingIterationId) {
      group.get('outingIterationId')?.setValue(this.outing.outingIterationId);
    }
    return group;
  }

  addRegistration(): void {
    if (this.registrations.length < 4 && this.outing) {
      const wasSingle = this.registrations.length === 1;
      const group = this.newRegistrationGroup();
      group.get('outingIterationId')?.setValue(this.outing.outingIterationId);
      this.registrations.push(group);
      if (wasSingle) {
        this.singlePlayerCaptainSub?.unsubscribe();
        this.registrationForm.get('teamCaptain')?.setValue('');
        this.selectedTeamCaptainIndex = -1;
      }
    }
  }

  removeRegistration(index: number): void {
    this.registrations.removeAt(index);
    if (this.registrations.length === 1 && this.outing) {
      this.registrationForm.get('teamCaptain')?.setValue('');
      this.selectedTeamCaptainIndex = -1;
      this.getTeamCaptains();
    }
  }

  setRegistrationType(type: RegistrationType): void {
    this.registrationType = type;
    this.selectedTeamCaptainIndex = -1;
    this.singleRegistrationTeamMode = 'solo_captain';
    this.singlePlayerCaptainSub?.unsubscribe();
    this.registrations.clear();
    if (type === 'individual') {
      this.registrations.push(this.newRegistrationGroup());
      this.registrationForm.get('teamCaptain')?.setValue('');
      this.subscribeSinglePlayerTeamCaptainSync();
    } else {
      this.registrations.push(this.newRegistrationGroup());
      this.registrations.push(this.newRegistrationGroup());
    }
  }

  continueToReview(): void {
    this.teamCaptainErrorMessage = null;
    this.applySinglePlayerTeamCaptain();
    if (this.registrations.length === 1) {
      if (this.allTeamCaptainSlotsFull) {
        this.teamCaptainErrorMessage = 'All teams are full. You cannot register as a single player right now.';
        return;
      }
      if (this.hasSelectableTeamCaptain) {
        if (this.singleRegistrationTeamMode === 'join_team') {
          const captain = this.registrationForm.get('teamCaptain')?.value;
          if (captain == null || String(captain).trim() === '') {
            this.teamCaptainErrorMessage = 'Select the team captain you are joining';
            return;
          }
        }
      }
    } else if (this.registrations.length > 1) {
      const captain = this.registrationForm.get('teamCaptain')?.value;
      if (!captain || String(captain).trim() === '') {
        this.teamCaptainErrorMessage = 'You must select a team captain';
        return;
      }
    }
    this.activeStep = 3;
  }

  backToEdit(): void {
    this.activeStep = 2;
  }

  markTeamCaptain(index: number): void {
    this.selectedTeamCaptainIndex = this.selectedTeamCaptainIndex === index ? -1 : index;
    if (this.selectedTeamCaptainIndex >= 0) {
      const reg = this.registrations.at(index).value;
      this.registrationForm.get('teamCaptain')?.setValue(`${reg.firstName} ${reg.lastName}`);
    } else {
      this.registrationForm.get('teamCaptain')?.setValue('');
    }
  }

  onSingleRegistrationTeamModeChange(mode: SingleRegistrationTeamMode): void {
    if (mode === 'solo_captain') {
      this.applySinglePlayerTeamCaptain();
    } else {
      this.registrationForm.get('teamCaptain')?.setValue('');
      this.selectedTeamCaptainIndex = -1;
    }
  }

  private subscribeSinglePlayerTeamCaptainSync(): void {
    this.singlePlayerCaptainSub?.unsubscribe();
    if (this.registrations.length !== 1) {
      return;
    }
    const g = this.registrations.at(0) as FormGroup;
    this.singlePlayerCaptainSub = g.valueChanges.subscribe(() => {
      if (this.registrations.length !== 1) {
        return;
      }
      if (!this.hasSelectableTeamCaptain || this.singleRegistrationTeamMode === 'solo_captain') {
        this.applySinglePlayerTeamCaptain();
      }
    });
  }

  /** Sets team captain to the sole golfer when leading a team, or when no join list exists. */
  private applySinglePlayerTeamCaptain(): void {
    if (this.registrations.length !== 1) {
      return;
    }
    if (this.hasSelectableTeamCaptain && this.singleRegistrationTeamMode === 'join_team') {
      return;
    }
    const reg = this.registrations.at(0) as FormGroup;
    const fn = String(reg.get('firstName')?.value ?? '').trim();
    const ln = String(reg.get('lastName')?.value ?? '').trim();
    if (!fn || !ln) {
      return;
    }
    this.registrationForm.get('teamCaptain')?.setValue(`${fn} ${ln}`);
    this.selectedTeamCaptainIndex = 0;
  }

  findOuting(): void {
    if (!this.registrationCode?.trim()) {
      this.setMessage('Please enter an outing code', 'error');
      return;
    }
    if (!this.apiKey) {
      this.setMessage('API key is required. Please access this widget with the correct URL.', 'error');
      return;
    }
    this.loadingStates.search = true;
    this.messages = null;
    this.isOutingPast = false;
    this.registrationWidgetService.getOutingByRegistrationCode(this.apiKey, this.registrationCode.trim()).subscribe({
      next: (result: any) => {
        if (!result || result === '') {
          this.setMessage('Unable to find outing, try again', 'error');
          this.outing = null;
          this.loadingStates.search = false;
          return;
        }
        const startDate = new Date(result.startDate);
        const now = new Date();
        const hasPassed = startDate < new Date(now.getFullYear(), now.getMonth(), now.getDate());
        if (hasPassed) {
          this.isOutingPast = true;
          this.setMessage(
            `This outing has already passed. The outing date was ${startDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}. Registration is no longer available.`,
            'error'
          );
          this.outing = null;
          this.loadingStates.search = false;
          return;
        }
        this.outing = result;
        this.setRegistrationType('individual');
        if (this.outing.outingIterationId) {
          this.registrations.at(0)?.get('outingIterationId')?.setValue(this.outing.outingIterationId);
        }
        this.loadTeamCaptains$()
          .pipe(finalize(() => (this.loadingStates.search = false)))
          .subscribe(() => {
            this.activeStep = 2;
          });
      },
      error: (err) => {
        this.loadingStates.search = false;
        this.outing = null;
        this.setMessage(err?.error?.message || 'Outing not found. Please check the code.', 'error');
      }
    });
  }

  reset(): void {
    this.singlePlayerCaptainSub?.unsubscribe();
    this.singleRegistrationTeamMode = 'solo_captain';
    this.registrationCode = '';
    this.outing = null;
    this.teamCaptains = [];
    this.registrations.clear();
    this.createdRegistrations = [];
    this.paymentIntent = null;
    this.playerPaymentIntent = null;
    this.showPaymentStep = false;
    this.registrationComplete = false;
    this.messages = null;
    this.activeStep = 1;
    this.isOutingPast = false;
    this.teamCaptainErrorMessage = null;
    this.isEmailModalOpen = false;
    this.isPaymentSuccessOpen = false;
    this.registrationForm.reset();
    this.elementsOptions = { appearance: { theme: 'stripe' }, locale: 'en' } as StripeElementsOptions;
  }

  payNow(): void {
    if (!this.validateTeamCaptainForSubmit()) {
      return;
    }
    this.teamCaptainErrorMessage = null;
    const teamCaptain = this.registrationForm.get('teamCaptain')?.value;

    const subtotalCents = Math.round((this.outing!.cost * this.registrations.length) * 100);
    let customerFeeCents = 0;
    if (this.outing!.creditCardFeeType === CreditCardFeeType.Percentage) {
      customerFeeCents = Math.round(subtotalCents * ((this.outing!.creditCardFee || 0) / 100));
    } else if (this.outing!.creditCardFeeType === CreditCardFeeType.Fixed) {
      customerFeeCents = Math.round((this.outing!.creditCardFee || 0) * 100);
    }
    const totalCostCents = subtotalCents + customerFeeCents;
    const applicationFeeCents = Math.round(totalCostCents * 0.03);

    this.loadingStates.submit = true;
    this.messages = null;

    this.registrationWidgetService.createPaymentIntent({
      accountId: this.outing!.stripeAccountId,
      options: {
        amount: totalCostCents,
        currency: 'usd',
        application_fee_amount: applicationFeeCents,
        metadata: {
          'Name(s)': this.registrations.value.map((r: any) => `${r.firstName} ${r.lastName}`).join(', '),
          OutingIterationId: this.outing!.outingIterationId,
          TeamCaptain: teamCaptain || '',
          OutingName: this.outing!.outingName || ''
        }
      }
    }, this.apiKey).subscribe({
      next: (pi: any) => {
        this.ngxStripeService.changeKey(environment.stripe.token, { stripeAccount: this.outing!.stripeAccountId });
        this.paymentIntent = pi;
        this.elementsOptions = {
          appearance: { theme: 'stripe' },
          locale: 'en',
          clientSecret: pi.client_secret?.toString() || pi.clientSecret?.toString()
        } as StripeElementsOptions;
        this.showPaymentStep = true;
      },
      error: (err) => {
        this.setMessage(err?.error?.message || 'Failed to initialize payment.', 'error');
      },
      complete: () => this.loadingStates.submit = false
    });
  }

  openEmailModal(): void {
    if (!this.validateTeamCaptainForSubmit()) {
      return;
    }
    this.teamCaptainErrorMessage = null;
    this.isEmailModalOpen = true;
  }

  submitEmailRegistration(): void {
    const teamCaptain = this.registrationForm.get('teamCaptain')?.value;
    const regs = this.registrations.value.map((r: any) => ({
      firstName: r.firstName,
      lastName: r.lastName,
      outingIterationId: this.outing!.outingIterationId,
      isCheckedIn: false,
      hasPaid: false,
      createdBy: this.userDetails.id
    }));

    this.emailSending = true;
    this.registrationWidgetService.createMultipleRegistrations({
      teamCaptain: teamCaptain,
      emailAddress: this.registrationForm.get('emailAddress')?.value,
      stripePaymentIntentId: this.paymentIntent?.id,
      registrations: regs,
    }, this.apiKey).subscribe({
      next: (result: any) => {

        const dto = {
          emailAddress: this.registrationForm.get('emailAddress')?.value,
          registrations: result.map((r: any) => ({
            firstName: r.firstName,
            lastName: r.lastName,
            playerRegistrationCode: r.playerRegistrationCode
          }))
        }

        this.registrationWidgetService.emailPlayerRegistrationCodes(dto, this.apiKey).subscribe({
          next: () => {
            this.isEmailModalOpen = false;
            this.emailSending = false;
            this.openAlertDialog('Registration Complete', 'Your registration codes have been sent to your email.');
            this.reset();
          },
          error: () => {
            this.emailSending = false;
            this.setMessage('Failed to send email. Please try again.', 'error');
          }
        });
      },
      error: (err) => {
        this.emailSending = false;
        this.setMessage(err?.error?.message || 'Failed to create registration.', 'error');
      }
    });
  }

  closeEmailModal(): void {
    this.isEmailModalOpen = false;
  }

  confirmPayment(): void {
    if (!this.paymentElement?.elements) {
      this.openAlertDialog('Payment Error', 'Payment form not initialized.');
      return;
    }

    this.loadingStates.paymentSubmit = true;
    this.stripe.confirmPayment({
      elements: this.paymentElement.elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required'
    }).subscribe({
      next: (result) => {
        if (result.error) {
          this.loadingStates.paymentSubmit = false;
          this.openAlertDialog('Payment Error', result.error.message || 'Payment failed. Please try again.');
        } else if (result.paymentIntent?.status === 'succeeded') {

          const paidRegistrations = this.registrations.value.map((reg: any) => ({
            firstName: reg.firstName,
            lastName: reg.lastName,
            outingIterationId: this.outing.outingIterationId,
            isCheckedIn: false,
            hasPaid: true,
            createdBy: this.userDetails.id,
            paymentType: PaymentType.Card
          }));

          this.registrationWidgetService.createMultipleRegistrations({
            teamCaptain: this.registrationForm.get('teamCaptain')?.value,
            stripePaymentIntentId: this.paymentIntent.id,
            registrations: paidRegistrations,
          }, this.apiKey).subscribe({
            next: (apiResult: any) => {
              const created = apiResult?.registrations || apiResult?.data || apiResult;
   
              this.createdRegistrations = Array.isArray(created)
                ? created.map((r: any) => ({
                    id: r.id,
                    playerRegistrationCode: r.playerRegistrationCode || '',
                    firstName: r.firstName,
                    lastName: r.lastName
                  }))
                : [{
                    id: apiResult.id,
                    playerRegistrationCode: apiResult.playerRegistrationCode || '',
                    firstName: apiResult.firstName,
                    lastName: apiResult.lastName
                  }];
              this.showPaymentStep = false;
              this.paymentIntent = null;
              this.loadingStates.paymentSubmit = false;
              this.isPaymentSuccessOpen = true;
            },
            error: (err) => {
              this.loadingStates.paymentSubmit = false;
              this.openAlertDialog(
                'Registration Error',
                `Your payment was received but we could not complete registration. Please contact the organizer with this stripe payment reference: ${result.paymentIntent?.id || 'payment'} and have them manually add your registration(s). ${err?.error?.message || ''}`
              );
              this.reset();
            }
          });
        } else {
          this.loadingStates.paymentSubmit = false;
          this.openAlertDialog('Payment Unsuccessful', result.paymentIntent?.status?.toString() || 'Unknown status');
          this.reset();
        }
      },
      error: (err) => {
        this.loadingStates.paymentSubmit = false;
        this.openAlertDialog('Payment Error', `There was an error processing your payment. ${err?.message || ''}`);
        this.reset();
      }
    });
  }

  closePaymentSuccess(): void {
    this.isPaymentSuccessOpen = false;
    this.createdRegistrations = [];
    this.reset();
  }

  cancelPayment(): void {
    if (this.paymentIntent) {
      this.registrationWidgetService.cancelPaymentIntent({
        connectedAccountId: this.outing!.stripeAccountId,
        paymentIntentId: this.paymentIntent.id,
        cancellationReason: 'abandoned'
      }, this.apiKey).subscribe({
        next: () => {
          this.paymentIntent = null;
          this.showPaymentStep = false;
          this.createdRegistrations = [];
          this.elementsOptions = { appearance: { theme: 'stripe' }, locale: 'en' } as StripeElementsOptions;
        },
        error: () => { this.paymentIntent = null; this.showPaymentStep = false; this.createdRegistrations = []; }
      });
    } else {
      this.showPaymentStep = false;
    }
  }

  onCardChange(event: any): void {
    this.isCardValid = event?.complete && !event?.error;
  }

  get playerPaymentAmount(): string {
    return this.playerRegistration?.cost != null
      ? (this.playerRegistration.cost / 100).toFixed(2)
      : '0.00';
  }

  searchPlayerRegistration(): void {
    if (!this.playerRegistrationCode?.trim()) {
      this.playerSearchError = 'Please enter a registration code';
      return;
    }
    this.playerSearchError = null;
    this.loadingStates.playerSearch = true;
    this.registrationWidgetService.getByPlayerRegistrationCode(this.apiKey, this.playerRegistrationCode.trim()).subscribe({
      next: (result: any) => {
        if (result) {
          if (typeof result.cost === 'number' && result.cost < 1000) {
            result.cost = result.cost * 100;
          }
          this.playerRegistration = result;
        } else {
          this.playerSearchError = 'Registration not found. Please check your code and try again.';
        }
      },
      error: () => {
        this.playerSearchError = 'Unable to find registration. Please check your code and try again.';
        this.playerRegistration = null;
      },
      complete: () => this.loadingStates.playerSearch = false
    });
  }

  resetPlayerSearch(): void {
    this.playerRegistrationCode = '';
    this.playerRegistration = null;
    this.playerSearchError = null;
    this.playerPaymentIntent = null;
    this.showPlayerPaymentStep = false;
    this.elementsOptions = { appearance: { theme: 'stripe' }, locale: 'en' } as StripeElementsOptions;
  }

  payPlayerRegistration(): void {
    if (!this.playerRegistration?.stripeAccountId) return;

    this.loadingStates.payment = true;
    const costCents = this.playerRegistration.cost;
    const creditCardFee = this.playerRegistration.creditCardFee ?? 0;
    const applicationFeeAmount = Math.round(costCents * (creditCardFee / 100));

    this.registrationWidgetService.createPaymentIntent({
      accountId: this.playerRegistration.stripeAccountId,
      options: {
        amount: costCents,
        currency: 'usd',
        application_fee_amount: applicationFeeAmount > 0 ? applicationFeeAmount : undefined,
        metadata: {
          RegistrationId: this.playerRegistration.id,
          PlayerRegistrationCode: this.playerRegistration.playerRegistrationCode
        }
      }
    }, this.apiKey).subscribe({
      next: (result: any) => {
        this.ngxStripeService.changeKey(environment.stripe.token, {
          stripeAccount: this.playerRegistration!.stripeAccountId
        });
        this.playerPaymentIntent = result;
        this.elementsOptions = {
          appearance: { theme: 'stripe' },
          locale: 'en',
          clientSecret: result.client_secret?.toString() || result.clientSecret?.toString()
        } as StripeElementsOptions;
        this.showPlayerPaymentStep = true;
      },
      error: (err) => {
        this.loadingStates.payment = false;
        this.playerSearchError = err?.error?.message || 'Failed to initialize payment. Please try again.';
      },
      complete: () => this.loadingStates.payment = false
    });
  }

  confirmPlayerPayment(): void {
    if (!this.paymentElement?.elements) {
      this.openAlertDialog('Payment Error', 'Payment form not initialized.');
      return;
    }

    this.loadingStates.paymentSubmit = true;
    this.stripe.confirmPayment({
      elements: this.paymentElement.elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required'
    }).subscribe({
      next: (result) => {
        if (result.error) {
          this.loadingStates.paymentSubmit = false;
          this.openAlertDialog('Payment Error', result.error.message || 'Payment failed. Please try again.');
        } else if (result.paymentIntent?.status === 'succeeded') {

          let registration = {
            id: this.playerRegistration.id,
            outingIterationId: this.playerRegistration.outingIterationId,
            firstName: this.playerRegistration.firstName,
            lastName: this.playerRegistration.lastName,
            isCheckedIn: false,
            hasPaid: true,
            playerRegistrationCode: this.playerRegistration.playerRegistrationCode,
            teamCaptain: this.playerRegistration.teamCaptain,
            paymentType: PaymentType.Card,
            stripePaymentIntentId: result.paymentIntent.id,
            lastEditedBy: this.userDetails.id,
          }

          this.registrationWidgetService.updatePlayerRegistration(this.playerRegistration!.id, registration, this.apiKey).subscribe({
            next: () => {
              this.showPlayerPaymentStep = false;
              this.openAlertDialog('Payment Successful', 'Your payment has been completed successfully.');
              this.searchPlayerRegistration();
            },
            error: () => {
              this.openAlertDialog('Payment Error', 'There was an error updating your registration. Please contact the organizer.');
            }
          });
        } else {
          this.loadingStates.paymentSubmit = false;
          this.openAlertDialog('Payment Unsuccessful', result.paymentIntent?.status?.toString() || 'Unknown status');
        }
      },
      error: (err) => {
        this.loadingStates.paymentSubmit = false;
        this.openAlertDialog('Payment Error', `There was an error processing your payment. ${err?.message || ''}`);
      }
    });
  }

  cancelPlayerPayment(): void {
    if (this.playerPaymentIntent) {
      this.registrationWidgetService.cancelPaymentIntent({
        connectedAccountId: this.playerRegistration!.stripeAccountId,
        paymentIntentId: this.playerPaymentIntent.id,
        cancellationReason: 'abandoned'
      }, this.apiKey).subscribe({
        next: () => {
          this.playerPaymentIntent = null;
          this.showPlayerPaymentStep = false;
          this.elementsOptions = { appearance: { theme: 'stripe' }, locale: 'en' } as StripeElementsOptions;
        },
        error: () => {
          this.playerPaymentIntent = null;
          this.showPlayerPaymentStep = false;
        }
      });
    } else {
      this.showPlayerPaymentStep = false;
    }
  }

  private validateTeamCaptainForSubmit(): boolean {
    this.applySinglePlayerTeamCaptain();
    const teamCaptain = this.registrationForm.get('teamCaptain')?.value;
    if (this.registrations.length === 1) {
      if (this.allTeamCaptainSlotsFull) {
        this.teamCaptainErrorMessage = 'All teams are full. You cannot register as a single player right now.';
        return false;
      }
      if (this.hasSelectableTeamCaptain && this.singleRegistrationTeamMode === 'join_team') {
        if (teamCaptain == null || String(teamCaptain).trim() === '') {
          this.teamCaptainErrorMessage = 'Select the team captain you are joining';
          return false;
        }
      } else if (!teamCaptain || String(teamCaptain).trim() === '') {
        this.teamCaptainErrorMessage = 'Enter your first and last name so we can record you as team captain.';
        return false;
      }
      return true;
    }
    if (this.registrations.length > 1 && (!teamCaptain || String(teamCaptain).trim() === '')) {
      this.teamCaptainErrorMessage = 'You must select a team captain';
      return false;
    }
    return true;
  }

  private setMessage(summary: string, severity: string): void {
    this.messages = { severity, summary };
  }

  private openAlertDialog(title: string, body: string): void {
    this.dialogRef = this.dialogService.open(AlertDialogPrimeNg, {
      header: title,
      width: '50vw',
      modal: true,
      breakpoints: { '960px': '75vw', '640px': '90vw' },
      data: { body, buttonText: 'Close' }
    });
    this.dialogRef.onClose.subscribe(() => {
      if (title.toLowerCase().includes('success') || title.toLowerCase().includes('complete')) {
        this.reset();
      }
    });
  }
}
